import api from '@common/api';
import logging from '@common/logging';
import { BankAccountsModel } from '@common/models/BankAccountsModel';
import { InvestorAccountModel } from '@common/models/InvestorAccountModel';
import { MyProfileModel } from '@common/models/MyProfileModel';
import { PropertiesModel } from '@common/models/PropertiesModel';
import { SubscriptionModel } from '@common/models/SubscriptionModel';
import storage from '@common/storage';
import support from '@common/support';
import { PricingPlan, SignatureMethod, SubscriptionOffer, InvestPreflightHint } from '@services/appsync/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import create from 'zustand';
import { PAGES } from '../constants';

interface CheckoutSFRViewModelProps {
  amount: number;
  amountAgreement: boolean;
  confirmAgreement: boolean;
  page: PAGES;
  property?: SubscriptionOffer;
  pricingPlan?: PricingPlan;
  propertyId?: string;
  newSignatureBlob?: Blob;
  newSignatureDataUrl?: string;
}

interface CheckoutSFRViewModelActions {
  getSuggestedAmounts: () => {
    minAmount: number;
    maxAmount: number;
    popularAmount: number;
    suggestedAmount: number;
    suggestedAmounts: number[];
  };
  handleAmountFormSubmit: () => Promise<AmountFormSubmitResult>;
  initController: (settings?: Partial<CheckoutSFRViewModelProps>) => Promise<void>;
  invest: () => Promise<void>;
  setAmount: (amount: number) => void;
  setAmountAgreement: (amountAgreement: boolean) => void;
  setConfirmAgreement: (confirmAgreement: boolean) => void;
  setNewSignatureBlob: (blog?: Blob) => void;
  setNewSignatureDataUrl: (dataUrl?: string) => void;
  setPage: (page: PAGES) => void;
  setPricingPlan: (pricingPlan: PricingPlan) => void;
}

interface AmountFormSubmitResult {
  success: boolean;
  error?: string;
  preflightHints?: InvestPreflightHint[];
}

const defaults: CheckoutSFRViewModelProps = {
  amount: 0,
  amountAgreement: false,
  confirmAgreement: false,
  newSignatureBlob: undefined,
  newSignatureDataUrl: undefined,
  page: PAGES.amount,
  pricingPlan: undefined,
  property: undefined,
};

export const useCheckoutSFRViewModel = create<CheckoutSFRViewModelProps & CheckoutSFRViewModelActions>((set, get) => ({
  ...defaults,
  getSuggestedAmounts: () => {
    const { property } = get();
    const maxAmount = property?.max_invest_amount ?? 100000000;
    const minAmount = property?.min_invest_amount ?? 10;
    const amountList = property?.suggested_invest_amounts ?? [];
    const suggestedAmounts = amountList.filter((amount: number) => amount <= maxAmount && amount >= minAmount);
    const popularAmount = suggestedAmounts[Math.ceil(suggestedAmounts.length / 2)];
    const suggestedAmount = amountList.length === 0 ? minAmount : popularAmount;
    return {
      maxAmount,
      minAmount,
      popularAmount,
      suggestedAmount,
      suggestedAmounts,
    };
  },
  handleAmountFormSubmit: async () => {
    const { amount, setPage, property } = get();
    const { investorAccount } = InvestorAccountModel.getState();
    try {
      const result = await api.getInvestPreflightInfo({
        amount,
        investor_account_id: investorAccount.id,
        subscription_offer_id: property.id,
      });
      const preflightHints = result?.hints ?? [];
      const retVal = { preflightHints, success: !preflightHints || preflightHints.length === 0 };

      if (retVal.success) {
        set({ amount });
        setPage(PAGES.confirmation);
      }
      return retVal;
    } catch (err: any) {
      logging.logError(err);
      return { error: 'System Error', success: false };
    }
  },
  initController: async (settings?: Partial<CheckoutSFRViewModelProps>) => {
    if (!settings?.propertyId) throw new Error('Property Not Found');
    const { checkSFREligibility, fetchInvestorAccount } = InvestorAccountModel.getState();
    const investorAccount = (await fetchInvestorAccount()).investorAccount ?? undefined;
    const amplifySession = await fetchAuthSession();
    if (!investorAccount) throw new Error('Investor account not found');
    if (!amplifySession.userSub) throw new Error('No user sub found');
    const { properties, fetchProperty, fetchProperties } = PropertiesModel.getState();
    if (!properties || properties.length === 0) await fetchProperties(investorAccount.id);
    const property = await fetchProperty(settings.propertyId, investorAccount.id);
    if (!property) throw new Error('Property Not Found');
    const { fetchBankAccounts } = BankAccountsModel.getState();
    const _bankAccounts = (await fetchBankAccounts()).bankAccounts ?? [];
    await checkSFREligibility({ bankAccounts: _bankAccounts });
    const { myProfile, fetchMyProfile, getStoredSignatureUrl } = MyProfileModel.getState();
    if (!myProfile) await fetchMyProfile(amplifySession.userSub);
    await getStoredSignatureUrl();
    const { suggestedAmount } = get().getSuggestedAmounts();

    // setup state
    set({
      ...defaults,
      ...{ amount: suggestedAmount },
      ...{ property },
      ...settings,
    });
  },
  invest: async () => {
    const { amount, property, newSignatureBlob, newSignatureDataUrl } = get();
    const { investorAccount, updateBrokerDealerAssociation } = InvestorAccountModel.getState();
    if (!investorAccount) throw new Error('Investor account not found');
    if (!property?.offering_id) throw new Error('Property Offering ID not found');

    // store signature
    if (newSignatureBlob) {
      const result = await storage.saveSignature(newSignatureBlob);
      if (!result) throw new Error('There was an error saving your signature. Please contact support.');
      // also update the signature url in the profile so we don't have to refetch it
      await MyProfileModel.getState().setStoredSignatureUrl(newSignatureDataUrl);
    }

    // if they haven't answered the broker dealer association question, set it to false
    if ((investorAccount?.non_accredited_survey_v2?.broker_dealer_assoc?.value ?? null) === null) {
      await updateBrokerDealerAssociation(false);
    }

    // submit one time investment
    try {
      const result = await api.invest({
        amount,
        investor_account_id: investorAccount.id,
        signature_method: SignatureMethod.INTERNAL,
        subscription_offer_id: property.id,
      });
      if (!result) throw new Error('There was an error submitting your investment. Please contact support.');
    } catch (err: any) {
      logging.logError(err, {
        context: {
          amount,
          investor_account_id: investorAccount.id,
          signature_method: SignatureMethod.INTERNAL,
          subscription_offer_id: property.id,
        },
      });
      throw new Error('There was an error submitting your investment. Please contact support.');
    }

    // refresh subscriptions
    await SubscriptionModel.getState().fetchSubscriptions(investorAccount.id);

    // track investment
    support.trackEvent('invest', { amount: String(amount) });

    // TODO: branch?

    // change page to congrats
    set({ page: PAGES.congrats });
  },
  setAmount: (amount: number) => {
    set({ amount: amount });
  },
  setAmountAgreement: (amountAgreement: boolean) => {
    set({ amountAgreement });
  },
  setConfirmAgreement: (confirmAgreement: boolean) => {
    set({ confirmAgreement });
  },
  setNewSignatureBlob: (blob?: Blob) => {
    set({ newSignatureBlob: blob });
  },
  setNewSignatureDataUrl: (dataUrl?: string) => {
    set({ newSignatureDataUrl: dataUrl });
  },
  setPage: (page: PAGES) => {
    set({ page });
  },
  setPricingPlan: (pricingPlan: PricingPlan) => {
    set({ pricingPlan });
  },
}));
